import { useCallback, useMemo } from 'react';

import { CampaignStoreItem, useShowCampaignById_new } from '@givebacks/api/store';
import { UseQueryResult } from '@tanstack/react-query';

type CampaignTranslationSelect = Pick<
  UseQueryResult<
    {
      team: CampaignStoreItem['team_options'];
      participant: CampaignStoreItem['participant_options'];
      benefit_options: CampaignStoreItem['benefit_options'];
    },
    unknown
  >,
  'data' | 'isLoading'
> & {
  isEnabled: boolean;
};

const useCampaignTranslations = (queryData: CampaignTranslationSelect) => {
  const { data, isLoading, isEnabled } = queryData;
  const TRANSLATIONS = useMemo(
    () => ({
      team: data?.team?.convention_singular,
      teams: data?.team?.convention_plural,
      participant: data?.participant?.convention_singular,
      participants: data?.participant?.convention_plural,
      benefits: data?.benefit_options?.title,
    }),
    [data]
  );
  const t = useCallback(
    (keyString: keyof typeof TRANSLATIONS) => {
      if (isLoading && isEnabled) return '';
      return TRANSLATIONS?.[keyString] || keyString;
    },
    [TRANSLATIONS, isLoading, isEnabled]
  );
  return { t, isLoading };
};

const useDonorTranslations = ({
  storeId,
  storeItemId,
}: {
  storeId?: string;
  storeItemId: string;
}) => {
  const { data: campaign, isLoading } = useShowCampaignById_new({
    campaignId: storeItemId,
    causeId: storeId,
  });
  const selector = useMemo(
    () =>
      campaign
        ? {
            team: campaign?.team_options,
            participant: campaign?.participant_options,
            benefit_options: campaign?.benefit_options,
          }
        : undefined,
    [campaign]
  );
  return useCampaignTranslations({
    data: selector,
    isLoading,
    isEnabled: !!storeItemId && !!storeId,
  });
};

const useManagementTranslations = ({
  storeId,
  storeItemId,
}: {
  storeId?: string;
  storeItemId?: string;
}) => {
  const { data, isLoading } = useShowCampaignById_new({
    campaignId: storeItemId,
    causeId: storeId,
    select: (campaign) => ({
      team: campaign.team_options,
      participant: campaign.participant_options,
      benefit_options: campaign.benefit_options,
    }),
  });
  return useCampaignTranslations({ data, isLoading, isEnabled: !!storeItemId && !!storeId });
};
export { useDonorTranslations, useManagementTranslations };
